import { useEffect } from "react"
import { useBoundContext } from "lib/@components/binding/use-bound-context"
import { getCurrentClientId, getCurrentToken } from "minimals-template/components/contexts/NognitoContext"
import { instanceId } from "lib/graphql/instance-id"
import { raise } from "library/local-events/index.mjs"
import { delay } from "lib/delay"

const INITIAL_BACKOFF = 1000
const MAX_BACKOFF = 15000
const processed = new Set()

// Single global connection — torn down and replaced when client changes
let activeConnection = null

export function useServerEventsLongPolling() {
    const { currentClient } = useBoundContext()
    useEffect(() => ensureConnection(currentClient), [currentClient])

    return { loading: false }
}

function ensureConnection(clientId) {
    if (activeConnection?.clientId === clientId) return
    activeConnection?.stop()
    const stop = startConnection()
    activeConnection = { clientId, stop }
}

function startConnection() {
    let running = true //
    let backoff = INITIAL_BACKOFF
    let lastId = 0

    requestCycle().catch(console.error)

    return () => {
        running = false
    }

    async function requestCycle() {
        while (running) {
            try {
                await requestMessages()
            } catch (e) {
                console.warn("Error in message queue", e)
            }
        }
    }

    async function requestMessages() {
        const params = Object.fromEntries(new URLSearchParams(window.location.search).entries())
        const token = await getCurrentToken()
        try {
            const response = await fetch(
                `${window.appProtocol || process.env.REACT_APP_PROTOCOL}://${
                    window.appServerLocation || process.env.REACT_APP_SERVER
                }/polling?token=${token}&lastId=${lastId}&instance=${instanceId}&client=${getCurrentClientId()}&source=${
                    params.share ?? ""
                }&href=${encodeURIComponent(window.location.href)}`,
                {
                    method: "GET",
                    credentials: "include",
                }
            )
            if (response.ok) {
                backoff = INITIAL_BACKOFF
                const events = await response.json()
                if (!Array.isArray(events)) {
                    console.error("Events were not an array")
                } else {
                    for (const event of events ?? []) {
                        if (!event) {
                            console.warn("Discarded event")
                        } else {
                            try {
                                if (event.serverEvents) {
                                    //console.log(event)
                                    const { serverId, incrementalId } = event.serverEvents
                                    lastId = Math.max(lastId, incrementalId)
                                    if (serverId && processed.has(serverId)) {
                                        continue
                                    }
                                    if (serverId) {
                                        processed.add(serverId)
                                    }
                                    try {
                                        raise(event.serverEvents.event, ...JSON.parse(event.serverEvents.message))
                                    } catch (e) {
                                        console.error("Event Error", e)
                                    }
                                }
                            } catch (e) {
                                console.warn("Unexpected error in event processing", e)
                            }
                        }
                    }
                }
            } else {
                await delay(backoff)
                backoff = Math.min(MAX_BACKOFF, backoff * 2)
            }
        } catch (e) {
            await delay(backoff)
            backoff = Math.min(MAX_BACKOFF, backoff + 250)
        }
    }
}
